import React, { useMemo, useState } from 'react';
import { Grid, FormControl, useTheme } from '@mui/material';
import AceEditor from 'react-ace';
import 'ace-builds/src-noconflict/theme-monokai';
import SampleParseSection from './SampleParseSection';
import useAPIResReqStore from '../useAPIResReqStore';
import { CurlValidationError } from '../../../errors/CurlValidationError';
import { validateCurlSyntax } from '../../../utils/CurlUtil';

const CURL_INTERNAL_ERROR = 'An unexpected error occurred while validating the cURL command.';

const CurlSetup = ({ setOnFocus, handleCurl, onChangeState }) => {
  const theme = useTheme();
  const aceTheme = theme.palette.mode === 'dark' ? 'monokai' : 'github';
  const [curlValidatorFailed, setCurlValidatorFailed] = useState(false);
  const [curlValidatorMessage, setCurlValidatorMessage] = useState('');

  const updateCurlValidatorStatus = (message, isCurlError) => {
    setCurlValidatorFailed(isCurlError);
    setCurlValidatorMessage(message);
  };

  const checkCurlSyntax = () => {
    const curlCommand = useAPIResReqStore.getState().requestData.curl;

    try {
      const message = validateCurlSyntax(curlCommand);
      updateCurlValidatorStatus(message, false);
    } catch (error) {
      if (error instanceof CurlValidationError) {
        updateCurlValidatorStatus(error.message, true);
      } else {
        updateCurlValidatorStatus(CURL_INTERNAL_ERROR, true);
      }
    }
  };

  const handleCurlChange = value => {
    useAPIResReqStore.getState().setRequestCurl(value);
    useAPIResReqStore.getState().setSampleCurl(value);
  };

  const handleSkipSampleChange = skip => {
    useAPIResReqStore.getState().setSkipSample(skip);
    if (!skip) {
      useAPIResReqStore.getState().removeKeyInPlaceholder(useAPIResReqStore.getState().requestData.rawResponsePlaceholderName);
    } else {
      if (useAPIResReqStore.getState().requestData.rawResponsePlaceholderName) {
        useAPIResReqStore.getState().addKeyInPlaceholder(
          useAPIResReqStore.getState().requestData.rawResponsePlaceholderName,
          'text'
        );
      }
    }
  };

  const handleRawResponsePlaceholderChange = value => {
    useAPIResReqStore.getState().removeKeyInPlaceholder(useAPIResReqStore.getState().requestData.rawResponsePlaceholderName);
    useAPIResReqStore.getState().setRawResponsePlaceholder(value);
    useAPIResReqStore.getState().addKeyInPlaceholder(
      useAPIResReqStore.getState().requestData.rawResponsePlaceholderName,
      'text'
    );
  };

  const editorOptions = useMemo(
    () => ({
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      enableSnippets: false,
      showLineNumbers: true,
      tabSize: 2,
    }),
    []
  );

  return (
    <Grid container className="api-editor-container">
      <Grid item xs={12} className="width-100">
        <FormControl className="form-control-full">
          <Grid item xs={12} className="setup-mode-label">
            cURL
          </Grid>
          <Grid item xs={12} className="width-100">
            <div id="aceditor" className="curl-editor">
              <AceEditor
                mode="sh"
                theme={aceTheme}
                width="100%"
                height="auto"
                minLines={10}
                maxLines={50}
                showPrintMargin={false}
                showGutter={true}
                wrapEnabled={true}
                highlightActiveLine={true}
                value={useAPIResReqStore.getState().requestData.curl}
                onChange={handleCurlChange}
                debounceChangePeriod={100}
                fontSize={16}
                setOptions={editorOptions}
                editorFocus={() => setOnFocus('editor')}
                onBlur={checkCurlSyntax}
              />
            </div>
          </Grid>
          {curlValidatorMessage && (
            <Grid item xs={12} className="margin-top-10">
              <div className={curlValidatorFailed ? 'curl-error' : 'curl-success'}>
                {curlValidatorMessage}
              </div>
            </Grid>
          )}
        </FormControl>
      </Grid>

      <SampleParseSection
        className="margin-top-20"
        skipSample={useAPIResReqStore.getState().isSkipSample()}
        onSkipSampleChange={handleSkipSampleChange}
        rawResponsePlaceholderName={useAPIResReqStore.getState().requestData.rawResponsePlaceholderName}
        onRawResponsePlaceholderChange={handleRawResponsePlaceholderChange}
        onFocus={setOnFocus}
        validateRequest={() => onChangeState && onChangeState(1, 1)}
        isNavigating={false}
      />
    </Grid>
  );
};

export default CurlSetup;
